/// <amd-module name="Wit/Search/InstantSearchProviders/BoardSuggestionsProvider" />
import * as React from "react";
import { ITfsPageService, IProjectData } from "Tfs/Platform/Page";
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { IReadonlyObservableArray } from "VSS/Core/Observable";
import { IVssPageContext, Services } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";
import { onClickFPS } from "VSS/Platform/FPS";
import { IMenuItem, IMenuItemDetails, MenuCell, MenuItemType } from "VSSUI/Menu";
import * as SearchRestClient from "Wit/Clients/Search/RestClient/Search";
import * as SearchContracts from "Wit/Clients/Search/Contracts/Search";
import { BaseInstantSearchProvider } from "InstantSearch/Shared/InstantSearchProviders/BaseInstantSearchProvider";
import { ResultRow } from "InstantSearch/Shared/ResultRow";
import { TelemetryWriter } from "InstantSearch/Shared/TelemetryWriter";
import { IMenuServiceContext, IBoardsMenuProps, IInstantSearchMenuService } from "InstantSearch/Shared/Types";
import * as Resources from "Wit/Search/Resources";

const maxResultRows = 5;
const maxResultRowsNonFilterMode = 3;

/**
 * Service for creating menu items for instant Board Search results.
 */
export class BoardSuggestionsProvider extends BaseInstantSearchProvider<SearchContracts.BoardSearchResponse> implements IInstantSearchMenuService<IMenuServiceContext> {
    protected _contributionId = "ms.vss-work-web.board-project-menu-provider";

    private _searchClient: SearchRestClient.ISearchRestClient;
    private _projectProperties: IProjectData | undefined;
    private _boardsMenuProps: { [id: string]: IBoardsMenuProps };

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initContribution(this._contributionId);
        this._searchClient = this.pageContext.getRestClient<SearchRestClient.ISearchRestClient>("TFS_WIT_SEARCH.ISearchRestClient");
    }

    protected _initContribution(contributionId: string): void {
        super._initContribution(contributionId);
        if (this._contribution) {
            this._boardsMenuProps = this._contribution.boardsMenuProps || {};
        }
    }

    protected _getMenuItems(menuId: string,
        context: IMenuServiceContext,
        invokeProvider: boolean,
        searchText: string
    ): IReadonlyObservableArray<IMenuItem> | null {
        if (invokeProvider && searchText) {
            this.getSuggestions(searchText, context, "RecentBoardSearchResultsFetched");
        } else {
            this._menuItems.splice(0, this._menuItems.length);
        }
        return this._menuItems;
    }

    protected _fetchResults(searchText: string, context: IMenuServiceContext): Promise<SearchContracts.BoardSearchResponse> {
        const projectId = this.projectProperties ? this.projectProperties.id : "";
        const { invokedProvidersProps } = context;
        const key = invokedProvidersProps && invokedProvidersProps.props ? invokedProvidersProps.props.key : undefined;
        const boardType = key ? key.substr(1) : "";
        const maxRows = key ? maxResultRows : maxResultRowsNonFilterMode; // If key is set, it is filtered mode.
        const instantSearchRequest: SearchContracts.BoardSearchRequest = {
            searchText: searchText,
            $skip: 0,
            $top: maxRows,
            includeFacets: false,
            $orderBy: [],
            filters: boardType ? { boardType: [boardType] } : {}
        };
        return this._searchClient.fetchBoardSearchResults(instantSearchRequest, projectId);
    }

    protected _onFetchSuccess(searchText: string, context: IMenuServiceContext, data: SearchContracts.BoardSearchResponse): void {
        const { telemetryWriter, onFocus, searchInput, invokedProvidersProps } = context;
        this._menuItems.splice(0, this._menuItems.length);

        if (this.shouldInvokeProvider(context) && !searchInput.queryText().trim()) {
            // Search text is empty. It got changed before fetching results. So, not updating menuItems.
            return;
        }

        const header = invokedProvidersProps && invokedProvidersProps.props
            ? invokedProvidersProps.props.header
            : Resources.BoardsHeader;
        const key = invokedProvidersProps && invokedProvidersProps.props
            ? invokedProvidersProps.props.key
            : undefined;

        if (data && data.results.length > 0) {
            // Add header
            this._menuItems.push({
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: header,
                className: "search-suggestion-header"
            });

            const boardResults = data.results;
            const maxRows = key ? maxResultRows : maxResultRowsNonFilterMode; // If key is set, it is filtered mode.
            const limitOfBoardResults = Math.min(maxRows, boardResults.length);
            for (let index = 0; index < limitOfBoardResults; index++) {
                const boardResult = boardResults[index];
                const project = boardResult.project;
                const boardType = boardResult.boardtype;
                const team = boardResult.team;
                const { iconName, routeContribution } = this._boardsMenuProps[boardType];
                this._menuItems.push({
                    id: "",
                    groupKey: this._groupKey,
                    rank: this._rank,
                    renderMenuCell: (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails): JSX.Element | undefined => {
                        details.onFocusItem = onFocus;
                        switch (menuCell) {
                            case MenuCell.PrimaryText:
                                const boardResultString = format("{0} {1}", team.name, boardType.toLowerCase());
                                return <ResultRow iconProps={{ iconName: iconName }} title={boardResultString}></ResultRow>;
                            default:
                                return undefined;
                        }
                    },
                    onActivate: (menuItem: IMenuItem, event: React.KeyboardEvent<HTMLElement>) => {
                        const params =
                        {
                            project: project.name,
                            teamName: team.name,
                            pivot: boardType.toLowerCase()
                        };
                        const url: string = routeUrl(this.pageContext, routeContribution, params);

                        if (event.ctrlKey || event.metaKey) {
                            const newWindow = window.open(url, "_blank");
                            if (newWindow && newWindow.opener) {
                                newWindow.opener = null;
                            }
                        } else {
                            onClickFPS(this.pageContext, url, true, event);
                        }
                        this._recordBoardResultsClickTelemetry(index, telemetryWriter);
                    }
                });
            }
        } else if ((data && data.results.length === 0) || !data) {
            // If this is quick navigation mode then show no suggestion message
            if (key) {
                const text = this.projectProperties
                    ? format(Resources.NoSearchSuggestionsInProjectMessage, this._getBoardTypeResourceText(key), this.projectProperties.name)
                    : format(Resources.NoSearchSuggestionsMessage, this._getBoardTypeResourceText(key));

                this._menuItems.push(
                    {
                        id: "",
                        groupKey: this._groupKey,
                        rank: this._rank,
                        itemType: MenuItemType.Header,
                        text: header,
                        className: "search-suggestion-header"
                    },
                    {
                        id: "",
                        groupKey: this._groupKey,
                        rank: this._rank,
                        itemType: MenuItemType.Header,
                        text: text,
                        className: "suggestion-message"
                    }
                );
                announce(text);
            }

            telemetryWriter.publish("NoSuggestionsFound", { "type": "BoardSearchSuggestions" });
        }
    }

    protected _onFetchFail(context: IMenuServiceContext, error: Error): void {
        const { invokedProvidersProps } = context;
        const header = invokedProvidersProps && invokedProvidersProps.props
            ? invokedProvidersProps.props.header
            : Resources.BoardsHeader;
        this._menuItems.splice(0, this._menuItems.length);

        this._menuItems.push(
            {
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: header,
                className: "search-suggestion-header"
            },
            {
                id: "",
                itemType: MenuItemType.Header,
                className: "suggestion-message",
                groupKey: this._groupKey,
                rank: this._rank,
                text: Resources.GenericErrorMessage
            }
        );
        announce(Resources.GenericErrorMessage);
    }

    private get projectProperties(): IProjectData | undefined {
        if (!this._projectProperties) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            this._projectProperties = tfsPageData ? tfsPageData.project : undefined;
        }
        return this._projectProperties;
    }

    private _getBoardTypeResourceText(key: string): string {
        switch (key) {
            case "/board":
                return Resources.BoardText;
            case "/sprint":
                return Resources.SprintText;
            case "/backlog":
                return Resources.BacklogText;
            default:
                return Resources.BoardText;
        }
    }

    private _recordBoardResultsClickTelemetry = (index: number, telemetryWriter: TelemetryWriter): void => {
        telemetryWriter.publish("ResultClicked", {
            "index": index,
            "resultType": "BoardSearchResults"
        });
    }
}

Services.add(
    "project-board-provider-service",
    { serviceFactory: BoardSuggestionsProvider }
);